/**
 * NotificationsModalContext
 *
 * Global controller for the notifications inbox modal. Provides a single
 * source of truth for "open/close the notifications modal" so the AppBar
 * bell, the `/notifications` route, and the realtime in-app toast click
 * handler all coordinate through one state machine instead of duplicating
 * `useState` flags.
 *
 * Mirrors the existing `AuthModalWrapper` / `UsernameModalWrapper`
 * patterns in `App.tsx` so future surfaces can simply call
 * `useNotificationsModal().open()` from anywhere in the tree.
 */

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

/**
 * Public API of the notifications modal controller. Kept intentionally
 * small so consumers (toast click, AppBar bell, deep-link route) can
 * rely on a stable, narrow contract.
 */
export interface NotificationsModalContextValue {
  /** Whether the notifications modal is currently rendered. */
  isOpen: boolean;
  /** Open the notifications modal. Idempotent. */
  open: () => void;
  /** Close the notifications modal. Idempotent. */
  close: () => void;
}

const NotificationsModalContext = createContext<NotificationsModalContextValue | undefined>(
  undefined,
);

interface NotificationsModalProviderProps {
  children: ReactNode;
}

/**
 * Wrap the app tree so any descendant can open/close the notifications
 * modal without lifting state into a particular page or component.
 *
 * The actual `<NotificationsModal />` instance is rendered by
 * `NotificationsModalWrapper` (a sibling component), which subscribes to
 * `isOpen` and avoids prop-drilling through the route tree.
 */
export const NotificationsModalProvider: React.FC<NotificationsModalProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const open = useCallback((): void => {
    setIsOpen(true);
  }, []);

  const close = useCallback((): void => {
    setIsOpen(false);
  }, []);

  const value = useMemo<NotificationsModalContextValue>(
    () => ({ isOpen, open, close }),
    [isOpen, open, close],
  );

  return (
    <NotificationsModalContext.Provider value={value}>
      {children}
    </NotificationsModalContext.Provider>
  );
};

/**
 * Consumer hook. Throws if called outside the provider so misuse fails
 * loudly instead of silently leaving the modal stuck closed.
 */
export const useNotificationsModal = (): NotificationsModalContextValue => {
  const context = useContext(NotificationsModalContext);
  if (!context) {
    throw new Error("useNotificationsModal must be used within a NotificationsModalProvider");
  }
  return context;
};
